import React, { useState, useEffect } from 'react'
import { dataService } from '@/lib/dataService'
import { useAuth } from '@/contexts/AuthContext'

export const TeacherSubjects: React.FC = () => {
  const { user } = useAuth()
  const [subjects, setSubjects] = useState<any[]>([])
  const [assignments, setAssignments] = useState<any[]>([])
  const [editingSubId, setEditingSubId] = useState<number | null>(null)
  const [editMaxPre, setEditMaxPre] = useState(10)
  const [editMaxPost, setEditMaxPost] = useState(10)
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [loading, setLoading] = useState(true)

  const loadData = async () => {
    if (!user) return
    setLoading(true)
    try {
      const [allSubjects, allAssignments] = await Promise.all([
        dataService.getSubjects(),
        dataService.getTeachingAssignments()
      ])
      setSubjects(allSubjects)
      setAssignments(allAssignments.filter((a: any) => a.teacher_id === user.id))
    } catch (err) {
      console.error('Error loading subject config:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [user])

  const handleSave = async (subjectId: number) => {
    try {
      await dataService.updateSubjectMaxScores(subjectId, editMaxPre, editMaxPost)
      setEditingSubId(null)
      setNotification({ type: 'success', message: '📈 ปรับปรุงค่าคะแนนเต็มรายวิชาสำเร็จแล้ว' })
      loadData()
    } catch (err: any) {
      setNotification({ type: 'error', message: err.message || 'เกิดข้อผิดพลาด' })
    }
  }

  const startEdit = (sub: any) => {
    setEditingSubId(sub.id)
    setEditMaxPre(sub.max_score_pre)
    setEditMaxPost(sub.max_score_post)
  }

  const mySubjects = subjects.filter(s => 
    assignments.map((a: any) => a.subject_id).includes(s.id)
  )

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid rgba(255, 255, 255, 0.1)',
          borderTopColor: 'var(--primary)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
        `}</style>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
      {/* Header */}
      <div>
        <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '8px' }}>📐 กำหนดคะแนนเต็มรายวิชา</h1>
        <p style={{ color: 'var(--text-secondary)' }}>กำหนดคะแนนเต็มสูงสุดของการประเมิน Pre-Test และ Post-Test ในรายวิชาที่รับผิดชอบ</p>
      </div>

      {notification && (
        <div style={{
          background: notification.type === 'success' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
          border: `1px solid ${notification.type === 'success' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)'}`,
          borderRadius: '8px',
          padding: '16px',
          color: notification.type === 'success' ? 'var(--success)' : 'var(--danger)',
          fontSize: '14px'
        }}>
          {notification.message}
        </div>
      )}

      <div className="glassmorphism" style={{ padding: '24px' }}>
        {mySubjects.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-secondary)' }}>
            📭 คุณครูยังไม่ได้ลงทะเบียนรายวิชาใดๆ ในขณะนี้
          </div>
        ) : (
          <div className="custom-table-container">
            <table className="custom-table">
              <thead>
                <tr>
                  <th>รหัสวิชา</th>
                  <th>ชื่อวิชาเรียน</th>
                  <th style={{ textAlign: 'center' }}>คะแนนเต็ม Pre-Test</th>
                  <th style={{ textAlign: 'center' }}>คะแนนเต็ม Post-Test</th>
                  <th style={{ textAlign: 'center' }}>การจัดการ</th>
                </tr>
              </thead>
              <tbody>
                {mySubjects.map((sub) => {
                  const isEditing = editingSubId === sub.id
                  return (
                    <tr key={sub.id}>
                      <td><code>{sub.code}</code></td>
                      <td><strong>{sub.name}</strong></td>
                      <td style={{ textAlign: 'center' }}>
                        {isEditing ? (
                          <input 
                            type="number" 
                            className="input-field"
                            value={editMaxPre}
                            onChange={(e) => setEditMaxPre(Number(e.target.value))}
                            style={{ width: '80px', textAlign: 'center', padding: '6px', margin: '0 auto' }}
                          />
                        ) : (
                          `${sub.max_score_pre} คะแนน`
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {isEditing ? (
                          <input 
                            type="number" 
                            className="input-field"
                            value={editMaxPost}
                            onChange={(e) => setEditMaxPost(Number(e.target.value))}
                            style={{ width: '80px', textAlign: 'center', padding: '6px', margin: '0 auto' }}
                          />
                        ) : (
                          `${sub.max_score_post} คะแนน`
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {isEditing ? (
                          <div style={{ display: 'inline-flex', gap: '8px' }}>
                            <button onClick={() => handleSave(sub.id)} className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: '12px' }}>✓ บันทึก</button>
                            <button onClick={() => setEditingSubId(null)} className="btn btn-outline" style={{ padding: '6px 12px', fontSize: '12px' }}>ยกเลิก</button>
                          </div>
                        ) : (
                          <button onClick={() => startEdit(sub)} className="btn btn-outline" style={{ padding: '6px 12px', fontSize: '12px' }}>✏️ แก้ไขคะแนนเต็ม</button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
export default TeacherSubjects
